import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { workorderContext } from '../contexts'
import { useTimezone } from '../contexts/timezone'
import {
    ApartmentDeadline,
    Inspection,
    LeaseCluster,
    Schedule,
    WorkorderCluster,
    WorkorderType,
} from '../models'
import { PropertyEvent } from '../models/PropertyEvent'
import {
    ActivityType,
    DailyActivityItem,
} from '../containers/DailyActivity/types'
import { useDeadline } from './deadline'
import { useLease } from './useLease'
import { usePropertyEvents } from './usePropertyEvents'
import { useTours } from './Outreach/useTours'
import { useSchedule } from './useSchedule'
import { useUser } from './useUser'
import { useAppDispatch } from './useAppDispatch'
import { getInspectionList, RootState } from '../store'
import { ApartmentTour } from '../models/Outreach/types'

const eachDayKeys = (start: DateTime, end: DateTime): string[] => {
    const keys: string[] = []
    let cursor = start.startOf('day')
    const last = end.startOf('day')
    if (!cursor.isValid || !last.isValid || cursor > last) {
        return keys
    }
    while (cursor <= last) {
        keys.push(cursor.toFormat('yyyy-MM-dd'))
        cursor = cursor.plus({ days: 1 })
    }
    return keys
}

const formatTimeRange = (
    start: DateTime,
    end?: DateTime | null,
): string | undefined => {
    if (!start.isValid) return undefined
    const startStr = start.toFormat('h:mm a')
    if (end && end.isValid) {
        return `${startStr} – ${end.toFormat('h:mm a')}`
    }
    return startStr
}

interface UseDailyActivityParams {
    /** ISO date (yyyy-MM-dd) for the Monday of the week to display */
    weekStart: string
}

export const useDailyActivity = (params: UseDailyActivityParams) => {
    const { currentTimezone } = useTimezone()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id

    const dateRange = useMemo(() => {
        const weekStartDt = DateTime.fromISO(params.weekStart, {
            zone: currentTimezone,
        }).startOf('day')
        const weekEndDt = weekStartDt.plus({ days: 6 }).endOf('day')
        return {
            startsAfter: weekStartDt.toISO()!,
            startsBefore: weekEndDt.toISO()!,
        }
    }, [params.weekStart, currentTimezone])

    const propertyEventsApi = usePropertyEvents({
        startsAfter: dateRange.startsAfter,
        startsBefore: dateRange.startsBefore,
    })
    const {
        events: propertyEvents,
        isLoading: propertyEventsLoading,
        createEvent,
        updateEvent,
        deleteEvent,
        draftMessage,
        sendEventMessage,
    } = propertyEventsApi

    const { tours, isLoading: toursLoading } = useTours({
        startsAfter: dateRange.startsAfter,
        startsBefore: dateRange.startsBefore,
    })

    const { getLeaseClusterList, leaseClusterList } = useLease()
    const { getWorkorderClusters, workorderClusters } = workorderContext()
    const deadlineApi = useDeadline()
    const {
        apartmentDeadlineList,
        getApartmentDeadlines,
        loadingState: deadlineLoading,
        getDeadlineDetail,
        transitionDeadline,
        incrementMessageCount,
        deleteApartmentDeadline,
        addApartmentDeadlines,
    } = deadlineApi
    const scheduleApi = useSchedule()
    const {
        scheduleList,
        getScheduleList,
        createSchedule,
        updateSchedule,
        syncBedStatuses,
        getUnitReport,
        loadingCreate,
        loadingUpdate,
    } = scheduleApi

    const dispatch = useAppDispatch()
    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    const [loadingSupport, setLoadingSupport] = useState(true)

    useEffect(() => {
        if (!apartmentId) return

        let cancelled = false
        setLoadingSupport(true)

        Promise.allSettled([
            getLeaseClusterList({ params: { exclude_children: true } }),
            getWorkorderClusters({ params: {} }),
            Promise.resolve(getApartmentDeadlines()),
            Promise.resolve(getScheduleList({})),
            dispatch(getInspectionList({ params: { active: true } })),
        ]).finally(() => {
            if (!cancelled) setLoadingSupport(false)
        })

        return () => {
            cancelled = true
        }
    }, [apartmentId])

    const items = useMemo(() => {
        const result: DailyActivityItem[] = []
        const rangeStart = DateTime.fromISO(dateRange.startsAfter).setZone(
            currentTimezone,
        )
        const rangeEnd = DateTime.fromISO(dateRange.startsBefore).setZone(
            currentTimezone,
        )
        const clipDays = (start: DateTime, end: DateTime) => {
            // Fully outside this week — do not pin onto Monday
            if (end < rangeStart || start > rangeEnd) {
                return []
            }
            const clippedStart = start < rangeStart ? rangeStart : start
            const clippedEnd = end > rangeEnd ? rangeEnd : end
            if (clippedStart > clippedEnd) {
                return []
            }
            return eachDayKeys(clippedStart, clippedEnd)
        }
        const isInRange = (dateKey: string) =>
            dateKey >= rangeStart.toFormat('yyyy-MM-dd') &&
            dateKey <= rangeEnd.toFormat('yyyy-MM-dd')

        propertyEvents.forEach((event: PropertyEvent) => {
            const startDt = DateTime.fromISO(event.starts_at, {
                zone: 'utc',
            }).setZone(currentTimezone)
            const endDt = DateTime.fromISO(event.ends_at, {
                zone: 'utc',
            }).setZone(currentTimezone)
            if (!startDt.isValid) return
            const eventDate = startDt.toFormat('yyyy-MM-dd')
            if (!isInRange(eventDate)) return

            result.push({
                id: `COMMUNITY_EVENT-${event.id}`,
                date: eventDate,
                type: 'COMMUNITY_EVENT',
                title: event.title,
                subtitle: event.location || undefined,
                time: event.is_all_day
                    ? undefined
                    : formatTimeRange(startDt, endDt),
                sortTime: startDt.toISO() ?? event.starts_at,
                color: event.status === 'DRAFT' ? '#9ca3af' : '#008C85',
                status: event.status === 'DRAFT' ? 'Draft' : 'Published',
                description: event.description || undefined,
                location: event.location || undefined,
                sourceData: event,
            })
        })

        const mapLeaseCluster = (
            cluster: LeaseCluster,
            type: 'MOVE_IN' | 'MOVE_OUT',
        ) => {
            const dateDt = DateTime.fromISO(cluster.date).setZone(
                currentTimezone,
            )
            const dateKey = dateDt.isValid
                ? dateDt.toFormat('yyyy-MM-dd')
                : cluster.date.slice(0, 10)
            if (!isInRange(dateKey)) return
            const label = type === 'MOVE_IN' ? 'Move Ins' : 'Move Outs'
            result.push({
                id: `${type}-${dateKey}`,
                date: dateKey,
                type,
                title: `${cluster.count} ${label}`,
                subtitle:
                    type === 'MOVE_OUT' && cluster.move_out_rule_count != null
                        ? `${cluster.move_out_rule_count} with move-out rules`
                        : undefined,
                sortTime: dateDt.isValid
                    ? dateDt.toISO() ?? cluster.date
                    : cluster.date,
                count: cluster.count,
                sourceData: cluster,
            })
        }

        ;(leaseClusterList?.move_in ?? []).forEach((c) =>
            mapLeaseCluster(c, 'MOVE_IN'),
        )
        ;(leaseClusterList?.move_out ?? []).forEach((c) =>
            mapLeaseCluster(c, 'MOVE_OUT'),
        )
        ;(workorderClusters ?? []).forEach(
            (cluster: WorkorderCluster, idx: number) => {
                let type: ActivityType
                if (cluster.workorder_type === WorkorderType.SERVICE_ORDER) {
                    type = 'SERVICE_ORDER'
                } else if (
                    cluster.workorder_type === WorkorderType.ACTION_ITEM
                ) {
                    type = 'ACTION_ITEM'
                } else if (
                    cluster.workorder_type === WorkorderType.SERVICE_REQUEST
                ) {
                    type = 'SERVICE_REQUEST'
                } else {
                    return
                }

                const startDt = DateTime.fromISO(cluster.start_date).setZone(
                    currentTimezone,
                )
                const endDt = DateTime.fromISO(
                    cluster.end_date ?? cluster.start_date,
                ).setZone(currentTimezone)
                if (!startDt.isValid) return

                const dayKeys = clipDays(startDt, endDt)
                dayKeys.forEach((dateKey) => {
                    result.push({
                        id: `${type}-${idx}-${dateKey}`,
                        date: dateKey,
                        type,
                        title: cluster.service?.name ?? type,
                        subtitle: `${cluster.count} workorder${
                            cluster.count !== 1 ? 's' : ''
                        }`,
                        sortTime: `${dateKey}T00:00:00`,
                        count: cluster.count,
                        color: cluster.service?.color,
                        clusterIndex: idx,
                        sourceData: cluster,
                    })
                })
            },
        )
        ;(apartmentDeadlineList ?? []).forEach(
            (aptDeadline: ApartmentDeadline) => {
                const deadline = aptDeadline.deadline
                if (!deadline?.date) return
                const dateDt = DateTime.fromISO(deadline.date).setZone(
                    currentTimezone,
                )
                const dateKey = dateDt.isValid
                    ? dateDt.toFormat('yyyy-MM-dd')
                    : deadline.date.slice(0, 10)
                if (!isInRange(dateKey)) return
                result.push({
                    id: `DEADLINE-${aptDeadline.id}`,
                    date: dateKey,
                    type: 'DEADLINE',
                    title: deadline.name,
                    subtitle: deadline.description || undefined,
                    sortTime: `${dateKey}T00:00:00`,
                    status: aptDeadline.complete_status ? 'Complete' : 'Open',
                    description: deadline.description || undefined,
                    sourceData: aptDeadline,
                })
            },
        )
        ;(scheduleList ?? []).forEach((schedule: Schedule) => {
            if (!schedule.active) return
            const startDt = DateTime.fromISO(schedule.start_date).setZone(
                currentTimezone,
            )
            const endDt = DateTime.fromISO(schedule.end_date).setZone(
                currentTimezone,
            )
            if (!startDt.isValid) return

            const dayKeys = clipDays(startDt, endDt.isValid ? endDt : startDt)
            dayKeys.forEach((dateKey) => {
                result.push({
                    id: `PROJECT-${schedule.id}-${dateKey}`,
                    date: dateKey,
                    type: 'PROJECT',
                    title: schedule.name,
                    subtitle: `${schedule.complete_count}/${schedule.assigned_count} complete`,
                    sortTime: `${dateKey}T00:00:00`,
                    color: schedule.color,
                    description: schedule.inspection_name || undefined,
                    sourceData: schedule,
                })
            })
        })
        ;(inspectionList ?? []).forEach((inspection: Inspection) => {
            if (!inspection.active) return
            const startDt = DateTime.fromISO(inspection.start_date).setZone(
                currentTimezone,
            )
            const endDt = DateTime.fromISO(inspection.end_date).setZone(
                currentTimezone,
            )
            if (!startDt.isValid) return

            const dayKeys = clipDays(startDt, endDt.isValid ? endDt : startDt)
            dayKeys.forEach((dateKey) => {
                const progress =
                    inspection.ui_total && inspection.ui_total > 0
                        ? `${inspection.ui_complete ?? 0}/${
                              inspection.ui_total
                          } complete`
                        : undefined
                result.push({
                    id: `INSPECTION-${inspection.id}-${dateKey}`,
                    date: dateKey,
                    type: 'INSPECTION',
                    title: inspection.name,
                    subtitle: progress,
                    sortTime: `${dateKey}T00:00:00`,
                    status:
                        inspection.ui_complete === inspection.ui_total &&
                        inspection.ui_total
                            ? 'Complete'
                            : 'Open',
                    description: inspection.inspection_type?.name || undefined,
                    sourceData: inspection,
                })
            })
        })

        const toursByDate: Record<string, ApartmentTour[]> = {}
        ;(tours ?? []).forEach((tour) => {
            if (!tour.starts_at) return
            const startDt = DateTime.fromISO(tour.starts_at, {
                zone: 'utc',
            }).setZone(currentTimezone)
            if (!startDt.isValid) return
            const dateKey = startDt.toFormat('yyyy-MM-dd')
            if (!isInRange(dateKey)) return
            if (!toursByDate[dateKey]) toursByDate[dateKey] = []
            toursByDate[dateKey].push(tour)
        })
        Object.entries(toursByDate).forEach(([dateKey, dayTours]) => {
            dayTours.sort((a, b) =>
                (a.starts_at ?? '').localeCompare(b.starts_at ?? ''),
            )
            const guides = Array.from(
                new Set(
                    dayTours
                        .map((t) => t.given_by?.name)
                        .filter(Boolean) as string[],
                ),
            )
            result.push({
                id: `TOUR-${dateKey}`,
                date: dateKey,
                type: 'TOUR',
                title: `${dayTours.length} Tour${
                    dayTours.length !== 1 ? 's' : ''
                }`,
                subtitle:
                    guides.length > 0
                        ? guides.slice(0, 2).join(', ') +
                          (guides.length > 2 ? ` +${guides.length - 2}` : '')
                        : undefined,
                sortTime: dayTours[0]?.starts_at ?? `${dateKey}T00:00:00`,
                count: dayTours.length,
                sourceData: dayTours,
            })
        })

        return result
    }, [
        propertyEvents,
        leaseClusterList,
        workorderClusters,
        apartmentDeadlineList,
        scheduleList,
        inspectionList,
        tours,
        currentTimezone,
        dateRange.startsAfter,
        dateRange.startsBefore,
    ])

    const isLoading =
        propertyEventsLoading ||
        toursLoading ||
        loadingSupport ||
        deadlineLoading.getList

    const reload = () => {
        getLeaseClusterList({ params: { exclude_children: true } })
        getWorkorderClusters({ params: {} })
        getApartmentDeadlines()
        getScheduleList({})
    }

    return {
        items,
        isLoading,
        timezone: currentTimezone,
        workspaceUser,
        workorderClusters: workorderClusters ?? [],
        apartmentDeadlineList: apartmentDeadlineList ?? [],
        reload,
        createEvent,
        updateEvent,
        deleteEvent,
        draftMessage,
        sendEventMessage,
        getDeadlineDetail,
        transitionDeadline,
        incrementMessageCount,
        deleteApartmentDeadline,
        addApartmentDeadlines,
        createSchedule,
        updateSchedule,
        syncBedStatuses,
        getUnitReport,
        scheduleLoading: loadingCreate || loadingUpdate,
    }
}
